<template>
  <div v-if="integration" class="flex items-center justify-center w-full">
    <div
      v-if="integration_name == 'mysql' || integration_name == 'postgres' || integration_name == 'duckdb' || integration_name == 'mongodb'"
      class="flex flex-col items-center justify-center w-full max-w-3xl mt-10 text-gray-900">
      <PostgresIcon v-if="integration_name === 'postgres'" class="my-auto mr-2 h-20" />
      <MySQLIcon v-else-if="integration_name === 'mysql'" class="my-auto mr-2 h-20" />
      <DuckDBIcon v-else-if="integration_name === 'duckdb'" class="my-auto mr-2 h-20" />
      <MongoDBIcon v-else-if="integration_name === 'mongodb'" class="my-auto mr-2 h-20" />
      <p class="text-xl text-gray-600">
        In order for the Platform to get access to your database, you have to provide the following information:
      </p>
      <p v-if="integration_name !== 'duckdb'" class="mt-1 italic text-gray-600 text-md">
        Tip: you can enter or paste your connection string directly below
        <span v-if="integration_name === 'postgres'">postgresql://UserName:Password@Hostname:5432/DatabaseName</span>
        <span v-if="integration_name === 'mysql'">mysql://UserName:Password@Hostname:3306/DatabaseName</span>
        <span v-if="integration_name === 'mongodb'">mongodb://UserName:Password@Hostname:27017/DatabaseName</span>
      </p>
      <p v-if="integration_name === 'duckdb'" class="mt-1 italic text-gray-600 text-md">
        Provide the path to your DuckDB file
      </p>

      <div class="mt-3">
        <Form
          ref="cred_form"
          autocomplete="off"
          :initial-values="initial_values"
          :validation-schema="schema"
          class="mt-2 space-y-6"
          @submit="authorizeDatabaseAccess">
          <div v-if="integration_name !== 'duckdb'" class="rounded-lg shadow-sm">
            <TextInput
              v-model="connection_string"
              classes="mb-4"
              name="connection_string"
              type="text"
              label="Connection String"
              placeholder="Enter your connection string"
              @input="parseConnectionString" />
            <div class="flex flex-row justify-between w-full gap-2">
              <div class="w-10/12">
                <TextInput classes="mb-4" name="instance_url" type="text" label="Host" />
              </div>
              <div class="w-2/12">
                <TextInput classes="mb-4 " name="db_port" type="text" label="Port" />
              </div>
            </div>
            <TextInput classes="mb-4" name="db_name" type="text" label="Database name" />
            <TextInput classes="mb-4" name="user_name" type="text" label="Username" />
            <TextInput classes="mb-2" name="access_token" type="password" label="Password" />
          </div>
          <div v-else class="rounded-lg shadow-sm">
            <TextInput classes="mb-4" name="instance_url" type="text" label="DuckDB File Path" />
          </div>
          <template v-if="integration_name !== 'duckdb'">
            <!-- SSH Settings Toggle -->
            <div class="mb-4 flex items-center">
              <input
                id="show_ssh_settings"
                v-model="initial_values.show_ssh_settings"
                type="checkbox"
                name="show_ssh_settings"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
              <label for="show_ssh_settings" class="ml-2 block text-sm font-medium text-gray-700"> Configure SSH Tunnel (Optional) </label>
            </div>

            <!-- SSH Configuration Panel -->
            <div v-if="initial_values.show_ssh_settings" class="rounded-lg shadow-sm border p-4 mb-4">
              <p class="text-lg text-gray-600 mb-3">
                SSH Configuration
              </p>
              <TextInput classes="mb-4" name="ssh_host" type="text" label="SSH Host" />
              <TextInput classes="mb-4" name="ssh_port" type="text" label="SSH Port" />
              <TextInput classes="mb-4" name="ssh_username" type="text" label="SSH Username" />
              <TextArea classes="mb-4" name="ssh_key" label="SSH Private Key" />
            </div>

            <!-- Connection arguments Toggle -->
            <div class="mb-4 flex items-center">
              <input
                id="show_arguments_settings"
                v-model="show_arguments_settings"
                type="checkbox"
                name="show_arguments_settings"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
              <label for="show_arguments_settings" class="ml-2 block text-sm font-medium text-gray-700">
                Connection arguments (Optional)
              </label>
            </div>

            <!-- Connection arguments Configuration Panel -->
            <div v-if="show_arguments_settings" class="rounded-lg shadow-sm border p-4 mb-4">
              <p class="text-lg text-gray-600 mb-3">
                Connection arguments Parameters
              </p>

              <!-- Existing metadata pairs -->
              <div v-if="metadata_pairs.length > 0" class="mb-4">
                <div v-for="(pair, index) in metadata_pairs" :key="index" class="flex flex-row gap-2 mb-2">
                  <div class="flex-1">
                    <TextInput
                      :id="'arguments-key-' + index"
                      v-model="pair.key"
                      :value="pair.key"
                      :classes="'mb-0'"
                      :name="'arguments-key-' + index"
                      :label="''"
                      type="text"
                      placeholder="Key" />
                  </div>
                  <div class="flex-1">
                    <TextInput
                      :id="'arguments-value-' + index"
                      v-model="pair.value"
                      :value="pair.value"
                      :classes="'mb-0'"
                      :name="'arguments-value-' + index"
                      :label="''"
                      type="text"
                      placeholder="Value" />
                  </div>
                  <div class="flex items-center mt-2">
                    <button
                      type="button"
                      class="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      @click="removeMetadataPair(index)">
                      Remove
                    </button>
                  </div>
                </div>
              </div>

              <!-- Add new metadata pair button -->
              <button
                type="button"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                @click="addMetadataPair">
                Add Parameter
              </button>

              <p class="mt-2 text-xs text-gray-500">
                These arguments will be included as query parameters in your connection string.
              </p>
            </div>
          </template>

          <p class="mt-0.5 text-xs text-gray-500">
            Note: Please be assured that passwords and private keys are securely encrypted and not stored in unencrypted format.
          </p>
          <button
            type="submit"
            class="inline-flex items-center justify-center w-full px-6 py-3 mt-5 text-base font-medium text-center text-white border border-transparent rounded-md shadow-sm bg-blue hover:bg-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light">
            <LockOpen class="w-5 h-5 mr-3 -ml-1" aria-hidden="true" />
            Authorize RevSetter To Access My
            {{
              integration_name === "postgres"
                ? "Postgres"
                : integration_name === "mysql"
                  ? "MySQL"
                  : integration_name === "mongodb"
                    ? "MongoDB"
                    : "DuckDB"
            }}
            Database
          </button>
        </Form>
      </div>
    </div>
    <div v-else-if="error_message" class="flex flex-col items-center justify-center w-full max-w-3xl mt-10">
      <div class="bg-red-100 border border-red-300 text-red-500 px-4 py-3 flex flex-col gap-2 rounded relative" role="alert">
        <div class="font-bold">
          Integration Access Error
        </div>
        <div class="block sm:inline">
          {{ error_message }}
        </div>
        <button
          type="button"
          class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
          @click="
            () => {
              $router.push('/admin/integrations/')
            }
          ">
          Retry Integration
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { LockOpen } from "lucide-vue-next"
import IntegrationService from "@/services/integration.service"
import PostgresIcon from "@/components/shared/icons/PostgresIcon.vue"
import MySQLIcon from "@/components/shared/icons/MySQLIcon.vue"
import DuckDBIcon from "@/components/shared/icons/DuckDBIcon.vue"
import MongoDBIcon from "@/components/shared/icons/MongoDBIcon.vue"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import * as Yup from "yup"
import { Form } from "vee-validate"

export default {
  name: "Authorization",
  components: {
    PostgresIcon,
    MySQLIcon,
    DuckDBIcon,
    MongoDBIcon,
    LockOpen,
    TextArea,
    TextInput,
    Form
  },

  data() {
    return {
      schema: null,
      error_message: null,
      initial_values: {
        show_ssh_settings: false
      },
      integration: null,
      integration_name: null,
      object_selection_url: null,
      show_arguments_settings: false,
      metadata_pairs: [],
      connection_string: "",
      meta_data: {},
      is_updating_from_connection_string: false,
      is_updating_from_fields: false,
      protocol: null
    }
  },
  watch: {
    // Deep watch form values to update connection string when they change
    "$refs.cred_form.values": {
      handler() {
        if (!this.is_updating_from_connection_string && this.$refs.cred_form) {
          this.updateConnectionStringFromFields()
        }
      },
      deep: true
    }
  },
  created() {
    let protocol_map = {
      postgres: "postgres",
      mysql: "mysql",
      duckdb: "duckdb",
      mongodb: "mongodb"
    }
    this.protocol = protocol_map[this.integration_name]
    this.object_selection_url = `/admin/integrations/${this.$route.params.integration_name}/${this.$route.params.integration_reference}/object-selection`

    this.schema = this.getSchema()

    this.initial_values = {
      instance_url: null,
      auth_type: "password",
      db_port: null,
      db_name: null,
      user_name: null,
      access_token: null,
      ssh_host: null,
      ssh_port: null,
      ssh_username: null,
      ssh_key: null,
      metadata: null,
      show_ssh_settings: false
    }
    this.integration_name = this.$route.params.integration_name

    IntegrationService.getIntegration(this.$route.params.integration_reference)
      .then((response) => {
        this.integration = response.data
        this.initial_values = response.data
        IntegrationService.getServiceObjects(this.$route.params.integration_reference)
          .then(() => {
            this.$router.push(this.object_selection_url)
          })
          .catch((err) => {
            console.error(err)
            this.error_message = err.message
          })
      })
      .catch((err) => {
        console.error(err)
        this.error_message =
          "We are unable to access the integration. This may be due to revoked access from the provider. Please check the integration settings or try again."
      })

    document.addEventListener("paste", (event) => {
      let paste = (event.clipboardData || window.clipboardData).getData("text")
      if (
        paste.startsWith("postgresql://") ||
        paste.startsWith("mysql://") ||
        paste.startsWith("mongodb://") ||
        paste.startsWith("mongodb+srv://")
      ) {
        // Update the connection string field and trigger parsing
        this.connection_string = paste
        this.parseConnectionString(paste)
      }
    })
  },
  methods: {
    // Update connection string based on form field values
    updateConnectionStringFromFields() {
      // Don't update if we're currently setting fields from a connection string
      if (!this.$refs.cred_form || !this.$refs.cred_form.values) {
        return
      }

      this.is_updating_from_fields = true

      const values = this.$refs.cred_form.values
      const host = values.instance_url
      const port = values.db_port
      const db_name = values.db_name
      const username = values.user_name
      const password = values.access_token

      // Make sure we have the minimum required fields
      if (!host || !db_name) {
        this.is_updating_from_fields = false
        return
      }

      // Build connection string based on database type
      let prefix = ""
      if (this.integration_name === "postgres") {
        prefix = "postgresql://"
      } else if (this.integration_name === "mysql") {
        prefix = "mysql://"
      } else if (this.integration_name === "mongodb") {
        prefix = "mongodb://"
      }

      // Build the auth part if username exists
      let auth = ""
      if (username) {
        auth = username
        if (password) {
          auth += ":" + password
        }
        auth += "@"
      }

      // Build the host:port part
      let host_port = host
      if (port) {
        host_port += ":" + port
      }

      // Build query string from metadata pairs
      let query_string = ""
      const valid_pairs = this.metadata_pairs.filter(
        (pair) => pair.key.trim() !== "" && pair.key.trim() !== "connection_string" // Skip the connection_string entry
      )

      if (valid_pairs.length > 0) {
        query_string = "?"
        query_string += valid_pairs
          .map((pair) => `${encodeURIComponent(pair.key.trim())}=${encodeURIComponent(pair.value.trim())}`)
          .join("&")
      }

      // Assemble the full connection string
      this.connection_string = prefix + auth + host_port + "/" + db_name + query_string

      // Reset flag after a short delay
      setTimeout(() => {
        this.is_updating_from_fields = false
      }, 100)
    },

    parseConnectionString(connection_string) {
      if (!connection_string) {
        return
      }

      const conn_str = typeof connection_string === "string" ? connection_string : this.connection_string

      if (
        conn_str.startsWith("postgresql://") ||
        conn_str.startsWith("mysql://") ||
        conn_str.startsWith("mongodb://") ||
        conn_str.startsWith("mongodb+srv://")
      ) {
        const is_mongo_srv = conn_str.startsWith("mongodb://") || conn_str.startsWith("mongodb+srv://")
        let url = conn_str.replace(/^(postgresql|mysql|mongodb(\+srv)?):\/\//, "")

        let [credentials, host_info] = url.split(/@([^@]+)$/)
        let [user, ...password_parts] = credentials.split(":")
        let password = password_parts.join(":")

        // Extract database name and query parameters
        let db_name_with_params,
          meta_data = null
        const host_info_parts = host_info.split("/")
        const host_port = host_info_parts[0]

        // For MongoDB, we concatenate all parts after the first "/" since database names can contain "/"
        if (this.integration_name === "mongodb" || is_mongo_srv) {
          db_name_with_params = host_info_parts.slice(1).join("/")
        } else {
          // For other databases, only use the first part after "/" as database name
          db_name_with_params = host_info_parts[1] || ""
        }

        // Handle query parameters if they exist for all database types
        if (db_name_with_params && db_name_with_params.includes("?")) {
          const [db_name, query_params] = db_name_with_params.split("?")
          if (query_params) {
            // Create a JSON object with the query parameters
            meta_data = {}
            query_params.split("&").forEach((param) => {
              const [key, value] = param.split("=")
              if (key && value) {
                meta_data[key] = value
              }
            })
            db_name_with_params = db_name

            // Also populate the arguments UI components
            this.metadata_pairs = Object.entries(meta_data).map(([key, value]) => ({ key, value }))
            this.show_arguments_settings = true
          }
        }

        const [host, port] = host_port.split(":")

        // Set default port based on database type
        let default_port
        if (this.integration_name === "mongodb" || is_mongo_srv) {
          default_port = "27017"
        } else if (this.integration_name === "mysql") {
          default_port = "3306"
        } else {
          default_port = "5432" // Default for PostgreSQL
        }

        // Set flag to prevent triggering updateConnectionStringFromFields
        this.is_updating_from_connection_string = true

        this.$refs.cred_form.setValues({
          instance_url: host,
          db_port: port || default_port,
          db_name: db_name_with_params || "",
          user_name: user,
          access_token: password,
          metadata: meta_data,
          connection_string: conn_str
        })

        this.connection_string = conn_str
        this.protocol = conn_str.split(":")[0]
        // Reset flag after a short delay to allow for form update
        setTimeout(() => {
          this.is_updating_from_connection_string = false
        }, 100)
      }
    },
    getSchema() {
      if (this.$route.params.integration_name === "duckdb") {
        return Yup.object().shape({
          instance_url: Yup.string().required("File path is required")
        })
      } else if (this.$route.params.integration_name === "mongodb") {
        return Yup.object().shape({
          instance_url: Yup.string().required("Host is required"),
          db_port: Yup.number().required("Port is required").default(27017),
          db_name: Yup.string()
            .matches(/^(\S+$)/, "* Database name cannot contain blank spaces")
            .required("Database name is required"),
          user_name: Yup.string()
            .matches(/^(\S+$)/, "* Username cannot contain blank spaces")
            .required("Username is required"),
          access_token: Yup.string().required("Password is required"),
          ssh_host: Yup.string().nullable(),
          ssh_port: Yup.number().nullable(),
          ssh_username: Yup.string().nullable(),
          ssh_key: Yup.string().nullable()
        })
      } else {
        return Yup.object().shape({
          instance_url: Yup.string().required("Host is required"),
          db_port: Yup.number().required("Port is required"),
          db_name: Yup.string()
            .matches(/^(\S+$)/, "* Database name cannot contain blank spaces")
            .required("Database name is required"),
          user_name: Yup.string()
            .matches(/^(\S+$)/, "* Username cannot contain blank spaces")
            .required("Username is required"),
          access_token: Yup.string().required("Password is required"),
          ssh_host: Yup.string().nullable(),
          ssh_port: Yup.number().nullable(),
          ssh_username: Yup.string().nullable(),
          ssh_key: Yup.string().nullable()
        })
      }
    },
    addMetadataPair() {
      this.metadata_pairs.push({ key: "", value: "" })
    },

    removeMetadataPair(index) {
      this.metadata_pairs.splice(index, 1)
      this.update_metadata_field()
    },

    update_metadata_field() {
      // Start with an object that includes the connection string if present
      const metadata = {
        protocol: this.protocol
      }

      // Add connection string to metadata if it exists
      if (this.connection_string) {
        metadata["connection_string"] = this.connection_string
      }

      // Filter out incomplete pairs
      const valid_pairs = this.metadata_pairs.filter((pair) => pair.key.trim() !== "")

      // Add all valid key-value pairs to the metadata object
      valid_pairs.forEach((pair) => {
        metadata[pair.key.trim()] = pair.value.trim()
      })

      // Update the form's metadata field
      if (Object.keys(metadata).length > 0) {
        this.$refs.cred_form.setFieldValue("metadata", metadata)
      } else {
        this.$refs.cred_form.setFieldValue("metadata", null)
      }
      this.meta_data = metadata
    },

    authorizeDatabaseAccess(values) {
      // Always update metadata to include connection string and any arguments
      this.update_metadata_field()

      let data = values
      if (this.integration_name === "duckdb") {
        data = {
          instance_url: values.instance_url
        }
      } else {
        data = {
          instance_url: values.instance_url,
          db_port: values.db_port,
          db_name: values.db_name,
          user_name: values.user_name,
          access_token: values.access_token,
          ssh_host: values.ssh_host,
          ssh_port: values.ssh_port,
          ssh_username: values.ssh_username,
          ssh_key: values.ssh_key,
          metadata: this.meta_data
        }
      }
      this.$store.dispatch("ui/showLoading")
      IntegrationService.setAccessToken(this.$route.params.integration_reference, data)
        .then(() => {
          this.$store.dispatch("ui/hideLoading")
          this.$router.push(this.object_selection_url)
        })
        .catch((err) => {
          console.error(err)
          this.$store.dispatch("ui/hideLoading")
          this.error_message = err.message
        })
    }
  }
}
</script>
